/**
 * Agregações para o dashboard (lembretes + clientes).
 */

import type { Reminder } from '@/core/domain';
import {
  isoToDateKey,
  normalizeSentAt,
} from '@/features/app/presentation/utils/reminder-day-metrics';

function hasScheduledDate(r: Reminder): boolean {
  return r.scheduled_date != null && String(r.scheduled_date).trim() !== '';
}

/** `YYYY-MM` do mês corrente (fuso local). */
export function currentMonthRef(): string {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
}

export function parseMonthRef(ref: string): { year: number; monthIndex: number } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(ref.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return { year, monthIndex: month - 1 };
}

export function monthRefLabelPt(ref: string): string {
  const p = parseMonthRef(ref);
  if (!p) return ref;
  return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(
    new Date(p.year, p.monthIndex, 1)
  );
}

/** Últimos `count` meses até hoje, `YYYY-MM` + rótulo. */
export function monthSelectOptions(count = 24): { ref: string; label: string }[] {
  const out: { ref: string; label: string }[] = [];
  const d = new Date();
  for (let i = 0; i < count; i += 1) {
    const y = d.getFullYear();
    const m = d.getMonth();
    const ref = `${y}-${String(m + 1).padStart(2, '0')}`;
    out.push({
      ref,
      label: new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(new Date(y, m, 1)),
    });
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

function isoInMonth(iso: string, year: number, monthIndex: number): boolean {
  const key = isoToDateKey(iso);
  if (key.length < 7) return false;
  const ym = key.slice(0, 7);
  const want = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
  return ym === want;
}

/** Total de disparos (cada entrada em sent_at) — histórico completo. */
export function totalSendEvents(reminders: Reminder[]): number {
  return reminders.reduce((acc, r) => acc + normalizeSentAt(r.sent_at).length, 0);
}

export function totalContactedReminders(reminders: Reminder[]): number {
  return reminders.filter((r) => r.contacted_at != null && String(r.contacted_at).trim() !== '').length;
}

export function totalScheduledReminders(reminders: Reminder[]): number {
  return reminders.filter((r) => r.scheduled_date != null && String(r.scheduled_date).trim() !== '').length;
}

/** Soma valor cobrado em lembretes com agendamento registrado. */
export function totalScheduledRevenue(reminders: Reminder[]): number {
  return reminders.reduce((acc, r) => {
    if (r.scheduled_date == null || String(r.scheduled_date).trim() === '') return acc;
    const v = r.value_charged;
    if (v == null || !Number.isFinite(v)) return acc;
    return acc + v;
  }, 0);
}

export interface MonthMetrics {
  sendEvents: number;
  contacted: number;
  scheduled: number;
  revenue: number;
}

export function metricsForMonth(reminders: Reminder[], year: number, monthIndex: number): MonthMetrics {
  let sendEvents = 0;
  for (const r of reminders) {
    for (const s of normalizeSentAt(r.sent_at)) {
      if (isoInMonth(s, year, monthIndex)) sendEvents += 1;
    }
  }
  const contacted = reminders.filter(
    (r) => r.contacted_at != null && String(r.contacted_at).trim() !== '' && isoInMonth(r.contacted_at, year, monthIndex)
  ).length;
  const scheduled = reminders.filter(
    (r) => r.scheduled_date != null && String(r.scheduled_date).trim() !== '' && isoInMonth(r.scheduled_date, year, monthIndex)
  ).length;
  const revenue = reminders.reduce((acc, r) => {
    if (r.scheduled_date == null || String(r.scheduled_date).trim() === '') return acc;
    if (!isoInMonth(r.scheduled_date, year, monthIndex)) return acc;
    const v = r.value_charged;
    if (v == null || !Number.isFinite(v)) return acc;
    return acc + v;
  }, 0);
  return { sendEvents, contacted, scheduled, revenue };
}

/** Dia do mês (1–31) → quantidade de envios naquele dia. */
export function sendEventsByDayOfMonth(
  reminders: Reminder[],
  year: number,
  monthIndex: number
): { day: number; count: number }[] {
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const buckets = new Array<number>(daysInMonth + 1).fill(0);
  for (const r of reminders) {
    for (const s of normalizeSentAt(r.sent_at)) {
      const key = isoToDateKey(s);
      if (key.length < 10) continue;
      const [y, m, d] = key.split('-').map(Number);
      if (y !== year || m - 1 !== monthIndex) continue;
      if (d >= 1 && d <= daysInMonth) buckets[d] += 1;
    }
  }
  return Array.from({ length: daysInMonth }, (_, i) => ({ day: i + 1, count: buckets[i + 1] ?? 0 }));
}

/** Manutenções com `scheduled_date` naquele dia do mês (uma contagem por lembrete). */
export function scheduledMaintenancesByDayOfMonth(
  reminders: Reminder[],
  year: number,
  monthIndex: number
): { day: number; count: number }[] {
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const buckets = new Array<number>(daysInMonth + 1).fill(0);
  for (const r of reminders) {
    if (!hasScheduledDate(r)) continue;
    const key = isoToDateKey(r.scheduled_date!);
    if (key.length < 10) continue;
    const [y, m, d] = key.split('-').map(Number);
    if (y !== year || m - 1 !== monthIndex) continue;
    if (d >= 1 && d <= daysInMonth) buckets[d] += 1;
  }
  return Array.from({ length: daysInMonth }, (_, i) => ({ day: i + 1, count: buckets[i + 1] ?? 0 }));
}

/** Por dia: disparos de envio + manutenções agendadas (mesmo mês). */
export function sendsAndScheduledMaintByDay(
  reminders: Reminder[],
  year: number,
  monthIndex: number
): { day: number; sends: number; maintenances: number }[] {
  const sends = sendEventsByDayOfMonth(reminders, year, monthIndex);
  const maint = scheduledMaintenancesByDayOfMonth(reminders, year, monthIndex);
  return sends.map((s, i) => ({
    day: s.day,
    sends: s.count,
    maintenances: maint[i]?.count ?? 0,
  }));
}

/** Data prevista de manutenção já passou e não há `scheduled_date` registrado. */
export function countOverdueMaintenances(reminders: Reminder[], todayKey: string): number {
  return reminders.filter((r) => {
    const exp = isoToDateKey(r.expected_date);
    if (exp >= todayKey) return false;
    if (hasScheduledDate(r)) return false;
    return true;
  }).length;
}

/** Já houve envio, manutenção prevista para hoje e contato ainda não marcado. */
export function countContactsDueToday(reminders: Reminder[], todayKey: string): number {
  return reminders.filter((r) => {
    if (isoToDateKey(r.expected_date) !== todayKey) return false;
    if (normalizeSentAt(r.sent_at).length === 0) return false;
    if (r.contacted_at != null && String(r.contacted_at).trim() !== '') return false;
    return true;
  }).length;
}

/** Data-chave da manutenção: agendada ou prevista. */
export function maintenanceDateKey(r: Reminder): string {
  if (r.scheduled_date != null && String(r.scheduled_date).trim() !== '') {
    return isoToDateKey(r.scheduled_date);
  }
  return isoToDateKey(r.expected_date);
}

export interface NextMaintenanceBundle {
  dateKey: string;
  reminders: Reminder[];
}

/** Próxima data de manutenção (>= hoje) e todos os lembretes nessa data. */
export function nextMaintenanceBundle(reminders: Reminder[], todayKey: string): NextMaintenanceBundle | null {
  const withKeys = reminders.map((r) => ({ r, key: maintenanceDateKey(r) }));
  const future = withKeys.filter(({ key }) => key >= todayKey);
  if (future.length === 0) return null;
  future.sort((a, b) => a.key.localeCompare(b.key));
  const firstKey = future[0].key;
  const list = future.filter((x) => x.key === firstKey).map((x) => x.r);
  return { dateKey: firstKey, reminders: list };
}

function isLembreteFuturo(r: Reminder, todayKey: string): boolean {
  return normalizeSentAt(r.sent_at).length === 0 && isoToDateKey(r.expected_date) >= todayKey;
}

/** A serem enviados, ordenados por data prevista. */
export function upcomingSends(reminders: Reminder[], todayKey: string, limit = 8): Reminder[] {
  return reminders
    .filter((r) => isLembreteFuturo(r, todayKey))
    .sort((a, b) => isoToDateKey(a.expected_date).localeCompare(isoToDateKey(b.expected_date)))
    .slice(0, limit);
}

function dateKeyFromLocalDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Segunda a domingo (inclusive, YYYY-MM-DD), semana atual e anterior — fuso local. */
export function localThisAndPrevWeekBounds(): {
  thisStart: string;
  thisEnd: string;
  prevStart: string;
  prevEnd: string;
} {
  const now = new Date();
  const dow = now.getDay();
  const diffToMon = dow === 0 ? -6 : 1 - dow;
  const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diffToMon);
  mon.setHours(0, 0, 0, 0);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  const prevSun = new Date(mon);
  prevSun.setDate(mon.getDate() - 1);
  const prevMon = new Date(prevSun);
  prevMon.setDate(prevSun.getDate() - 6);
  return {
    thisStart: dateKeyFromLocalDate(mon),
    thisEnd: dateKeyFromLocalDate(sun),
    prevStart: dateKeyFromLocalDate(prevMon),
    prevEnd: dateKeyFromLocalDate(prevSun),
  };
}

export function countSendEventsInDateRange(
  reminders: Reminder[],
  startKey: string,
  endKey: string
): number {
  let n = 0;
  for (const r of reminders) {
    for (const s of normalizeSentAt(r.sent_at)) {
      const k = isoToDateKey(s);
      if (k.length >= 10 && k >= startKey && k <= endKey) n += 1;
    }
  }
  return n;
}

/** Manutenções (data agendada ou prevista) em um dia. */
export function countMaintenancesOnDay(reminders: Reminder[], dayKey: string): number {
  return reminders.filter((r) => {
    if (r.status === 'cancelled') return false;
    return maintenanceDateKey(r) === dayKey;
  }).length;
}

export function countMaintenancesInDateRange(
  reminders: Reminder[],
  startKey: string,
  endKey: string
): number {
  return reminders.filter((r) => {
    if (r.status === 'cancelled') return false;
    const k = maintenanceDateKey(r);
    return k >= startKey && k <= endKey;
  }).length;
}

/**
 * Contatos em atraso: data prevista já passou, sem contato e sem agendamento registrado.
 */
export function countContactsOverdue(reminders: Reminder[], todayKey: string): number {
  return reminders.filter((r) => {
    if (r.status === 'cancelled') return false;
    if (hasScheduledDate(r)) return false;
    if (r.contacted_at != null && String(r.contacted_at).trim() !== '') return false;
    return isoToDateKey(r.expected_date) < todayKey;
  }).length;
}

export function percentChangeRatio(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? null : 100;
  return Math.round(((current - previous) / previous) * 100);
}

/** Taxa no mês: entre lembretes com contato registrado naquele mês, % com agendamento. */
export function conversionContactedToScheduledForMonth(
  reminders: Reminder[],
  year: number,
  monthIndex: number
): number | null {
  const contactedInMonth = reminders.filter((r) => {
    if (r.status === 'cancelled') return false;
    if (!r.contacted_at || String(r.contacted_at).trim() === '') return false;
    return isoInMonth(r.contacted_at, year, monthIndex);
  });
  if (contactedInMonth.length === 0) return null;
  const withSched = contactedInMonth.filter((r) => hasScheduledDate(r)).length;
  return Math.round((withSched / contactedInMonth.length) * 100);
}

export type DashboardPipelineLabel = 'pendente' | 'contatado' | 'agendado';

export function pipelineStatusFromReminder(r: Reminder): DashboardPipelineLabel {
  if (hasScheduledDate(r)) return 'agendado';
  if (r.contacted_at != null && String(r.contacted_at).trim() !== '') return 'contatado';
  return 'pendente';
}

/** Próximos lembretes por `expected_date` (≥ hoje), ordenados. */
export function nextFiveByExpectedDate(reminders: Reminder[], todayKey: string, limit = 5): Reminder[] {
  return reminders
    .filter((r) => r.status !== 'cancelled' && isoToDateKey(r.expected_date) >= todayKey)
    .sort((a, b) => isoToDateKey(a.expected_date).localeCompare(isoToDateKey(b.expected_date)))
    .slice(0, limit);
}

export interface DashboardSendFailureAlertRow {
  reminderId: string;
  message: string;
  timeLabel: string;
}

export function dashboardSendFailureAlerts(
  reminders: Reminder[],
  formatClientName: (clientId: string) => string
): DashboardSendFailureAlertRow[] {
  const rows: { reminderId: string; message: string; at: string }[] = [];
  for (const r of reminders) {
    const failures = r.send_failures ?? [];
    if (failures.length === 0) continue;
    const last = failures.reduce((a, b) => (a.at >= b.at ? a : b));
    const name = formatClientName(r.client_id);
    const detail = last.message?.trim();
    const message = detail ? `Falha no envio (${detail}) — ${name}` : `Falha no envio — ${name}`;
    rows.push({ reminderId: r.id, message, at: last.at });
  }
  rows.sort((a, b) => b.at.localeCompare(a.at));
  return rows.map(({ reminderId, message, at }) => ({
    reminderId,
    message,
    timeLabel: formatRelativeTimeShortPt(at),
  }));
}

function formatRelativeTimeShortPt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return 'Agora';
  const h = Math.floor(diffMs / 3600000);
  if (h < 1) return 'Agora há pouco';
  if (h < 24) return `Há ${h}h`;
  const days = Math.floor(h / 24);
  if (days === 1) return 'Ontem';
  return `Há ${days} dias`;
}

/** Segunda (inclusive) aos próximos 7 dias — chaves YYYY-MM-DD no fuso local. */
export function weekDayKeysFromMonday(mondayKey: string): string[] {
  const parts = mondayKey.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return [];
  const [y, m, d0] = parts;
  const keys: string[] = [];
  for (let i = 0; i < 7; i += 1) {
    const dt = new Date(y, m - 1, d0 + i);
    keys.push(dateKeyFromLocalDate(dt));
  }
  return keys;
}

/** Rótulo curto do dia da semana + dia (ex.: SEG 07). */
export function weekdayShortLabelPt(dayKey: string): string {
  const d = new Date(`${dayKey}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dayKey;
  const wd = new Intl.DateTimeFormat('pt-BR', { weekday: 'short' }).format(d);
  const day = String(d.getDate()).padStart(2, '0');
  const w = wd.replace('.', '').toUpperCase();
  return `${w} ${day}`;
}

/** Intervalo da semana legível (ex.: 3 a 9 de março de 2026). */
export function weekRangeLabelPt(weekStart: string, weekEnd: string): string {
  const a = new Date(`${weekStart}T12:00:00`);
  const b = new Date(`${weekEnd}T12:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '';
  const sameMonth = a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  const fmtDay = (d: Date) => d.getDate();
  const fmtMonthYear = (d: Date) =>
    new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(d);
  if (sameMonth) {
    return `${fmtDay(a)} a ${fmtDay(b)} de ${fmtMonthYear(b)}`;
  }
  return `${a.toLocaleDateString('pt-BR')} a ${b.toLocaleDateString('pt-BR')}`;
}

export type WeekAgendaKind = 'send' | 'maintenance_scheduled' | 'maintenance_expected';

export interface WeekAgendaItem {
  id: string;
  dayKey: string;
  sortKey: string;
  timeLabel: string;
  kind: WeekAgendaKind;
  clientName: string;
}

/**
 * Itens da agenda da semana (seg–dom): envios WhatsApp com data na semana
 * e manutenções (data prevista ou agendada) na semana.
 */
export function buildWeekAgenda(
  reminders: Reminder[],
  weekStart: string,
  weekEnd: string,
  getClientName: (clientId: string) => string
): WeekAgendaItem[] {
  const items: WeekAgendaItem[] = [];

  for (const r of reminders) {
    if (r.status === 'cancelled') continue;

    const sends = normalizeSentAt(r.sent_at);
    for (let i = 0; i < sends.length; i += 1) {
      const iso = sends[i];
      const k = isoToDateKey(iso);
      if (k.length < 10 || k < weekStart || k > weekEnd) continue;
      const d = new Date(iso);
      const timeLabel = Number.isNaN(d.getTime())
        ? '—'
        : d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      const sortKey = `${k} ${timeLabel} ${r.id} send${i}`;
      items.push({
        id: `send-${r.id}-${i}-${k}`,
        dayKey: k,
        sortKey,
        timeLabel,
        kind: 'send',
        clientName: getClientName(r.client_id),
      });
    }

    const mk = maintenanceDateKey(r);
    if (mk.length >= 10 && mk >= weekStart && mk <= weekEnd) {
      const hasSched = hasScheduledDate(r);
      const sortKey = `${mk} 12:00 ${r.id} maint`;
      items.push({
        id: `maint-${r.id}-${mk}`,
        dayKey: mk,
        sortKey,
        timeLabel: '—',
        kind: hasSched ? 'maintenance_scheduled' : 'maintenance_expected',
        clientName: getClientName(r.client_id),
      });
    }
  }

  items.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  return items;
}
